import { Button } from "@chakra-ui/react";
import { useCreditFacilityLoanSettingsTableDataRetrieveSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { IDRowMetadata } from "gen/model/iDRowMetadata";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const CreditFacilitySettingsLoans = () => {
  const { t } = useTranslation(["creditFacility", "common"]);
  const navigate = useNavigate();
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();

  const { data: loanTableData } =
    useCreditFacilityLoanSettingsTableDataRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
    );

  const creditFacilitySettingsRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .setup({})
    .creditFacilities({});

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      const loanId = (metadata as IDRowMetadata).id;
      return {
        type: "generic",
        to: creditFacilitySettingsRoute.creditFacilityLoanVersionPage({
          creditFacilityId,
          loanId,
        }).$,
      };
    },
    [creditFacilityId, creditFacilitySettingsRoute],
  );

  return (
    <PageTabBody>
      <TableBox isFullHeight={true}>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("creditFacility:settings.loans.title")}
          </TableBoxTitle>
          <DataTableBoxActions>
            <Restricted permission={PermissionTypeEnum.write}>
              <Button
                size="xs"
                colorScheme="secondary"
                title="Add Loan"
                onClick={() =>
                  navigate(
                    creditFacilitySettingsRoute.creditFacilityLoanCreate({
                      creditFacilityId,
                    }).$,
                  )
                }
              >
                Add Loan
              </Button>
            </Restricted>
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={loanTableData}
            getLinkInfoForRow={getLinkInfoForRow}
          />
        </TableBoxBody>
      </TableBox>
    </PageTabBody>
  );
};
